export interface AppBuildIdentity {
  releaseKey: string;
  release: string;
  deploymentId: string;
  commitSha: string;
  buildTime: string;
  environment: string;
}

function clean(value: unknown): string {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function fallbackBuildTime(): string {
  if (typeof __APP_BUILD_TIME__ !== 'undefined') return clean(__APP_BUILD_TIME__);
  return '';
}

function fallbackCommit(): string {
  if (typeof __APP_GIT_COMMIT__ !== 'undefined') return clean(__APP_GIT_COMMIT__);
  return '';
}

export function getCurrentAppBuildIdentity(): AppBuildIdentity {
  const windowBuild = typeof window !== 'undefined'
    ? {
        release: clean(window.__BUILD_RELEASE__),
        commitSha: clean(window.__BUILD_COMMIT_SHA__),
        environment: clean(window.__APP_ENV__),
      }
    : { release: '', commitSha: '', environment: '' };

  const release = windowBuild.release || clean(import.meta.env.VITE_RELEASE);
  const deploymentId = clean(import.meta.env.VITE_DEPLOYMENT_ID);
  const commitSha = windowBuild.commitSha || clean(import.meta.env.VITE_BUILD_COMMIT_SHA) || fallbackCommit();
  const buildTime = fallbackBuildTime();
  const environment = windowBuild.environment || clean(import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'production');
  const releaseKey = clean(deploymentId || commitSha || release || buildTime);

  return {
    releaseKey,
    release,
    deploymentId,
    commitSha,
    buildTime,
    environment,
  };
}

export function normaliseAppBuildIdentity(input: unknown): AppBuildIdentity | null {
  if (!input || typeof input !== 'object') return null;
  const value = input as Record<string, unknown>;
  const release = clean(value.release);
  const deploymentId = clean(value.deploymentId);
  const commitSha = clean(value.commitSha);
  const buildTime = clean(value.buildTime);
  const releaseKey = clean(value.releaseKey || deploymentId || commitSha || release || buildTime);
  if (!releaseKey || releaseKey.toLowerCase() === 'local' || releaseKey.toLowerCase() === 'unknown') return null;

  return {
    releaseKey,
    release,
    deploymentId,
    commitSha,
    buildTime,
    environment: clean(value.environment),
  };
}

export function isDifferentAppBuild(remote: AppBuildIdentity | null, current: AppBuildIdentity = getCurrentAppBuildIdentity()): boolean {
  if (!remote) return false;
  if (remote.deploymentId && current.deploymentId) return remote.deploymentId !== current.deploymentId;
  if (remote.commitSha && current.commitSha) return remote.commitSha !== current.commitSha;
  if (remote.releaseKey && current.releaseKey) return remote.releaseKey !== current.releaseKey;
  if (remote.buildTime && current.buildTime) return remote.buildTime !== current.buildTime;
  return false;
}

export async function fetchLatestAppBuildIdentity(): Promise<AppBuildIdentity | null> {
  const response = await fetch(`/app-version.json?ts=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  const data = await response.json();
  return normaliseAppBuildIdentity(data);
}
